import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import io from "socket.io-client";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  Alert,
  Stack,
  Divider,
  Box
} from "@mui/material";

import usePaymentPolling from "./hooks/usePaymentPolling";
import PaymentStatusBanner from "./components/PaymentStatusBanner";
import { fetchPerformanceById } from "./services/performanceService";

const API_URL = process.env.REACT_APP_API_URL;
const socket = io(API_URL, { autoConnect: false });

const PerformanceBookingFormModal = ({ performance, onClose, onBooked }) => {
  const [performanceData, setPerformanceData] = useState(performance);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Fetch full performance details when modal mounts
  useEffect(() => {
    if (!performance?._id) return;

    const fetchDetails = async () => {
      try {
        setIsLoadingDetails(true);
        setLoadError(null);
        const fullPerformance = await fetchPerformanceById(performance._id);
        if (fullPerformance) {
          setPerformanceData(fullPerformance);
        }
      } catch (error) {
        console.error("Error fetching performance details:", error);
        setLoadError("Failed to load performance details");
        setPerformanceData(performance);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [performance?._id, performance]);
  const [bookingType, setBookingType] = useState("single"); // "single" or "multiple"
  const [form, setForm] = useState({
    clientName: "",
    phone: "",
    email: "",
    bookingDate: "",
    endDate: "",
    from: "",
    to: "",
    eventDetails: "",
  });

  const [duration, setDuration] = useState(0);
  const [total, setTotal] = useState(0);
  const [reservationFee, setReservationFee] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("mpesa");

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [bookingStatus, setBookingStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");

  const [lastBookingId, setLastBookingId] = useState(null);
  const [checkoutRequestId, setCheckoutRequestId] = useState(null);
  const [pollingKey, setPollingKey] = useState(0);

  useEffect(() => {
    if (!socket.connected) socket.connect();

    const handleSocketUpdate = ({ id, status, reason, mpesaCode }) => {
      if (id !== lastBookingId) return;

      if (status === "Success") {
        setBookingStatus("success");
        if (mpesaCode) setConfirmationCode(mpesaCode);
        setSuccessMessage("Booking confirmed! Confirmation sent to your email.");
        setTimeout(() => {
          onClose?.();
          onBooked?.();
        }, 9000);
      } else if (status === "Failed") {
        setBookingStatus("failed");
        setErrors({ general: reason || "Payment failed." });
      }

      setLoading(false);
    };

    socket.on("paymentUpdate", handleSocketUpdate);
    return () => socket.off("paymentUpdate", handleSocketUpdate);
  }, [lastBookingId, onClose, onBooked]);

  useEffect(() => {
    if (bookingType === "single") {
      const { from, to } = form;
      if (from && to) {
        const start = new Date(`1970-01-01T${from}`);
        const end = new Date(`1970-01-01T${to}`);
        const hours = (end - start) / (1000 * 60 * 60);

        if (hours > 0) {
          setDuration(hours);
          const totalCost = hours * Number(performanceData.charges || 0);
          setTotal(totalCost);
          setReservationFee(Math.ceil(totalCost * 0.1));
        } else {
          setDuration(0);
          setTotal(0);
          setReservationFee(0);
        }
      }
    } else if (bookingType === "multiple") {
      const { bookingDate, endDate } = form;
      if (bookingDate && endDate) {
        const start = new Date(bookingDate);
        const end = new Date(endDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive count

        if (days > 0 && days <= 5) {
          setDuration(days);
          const hours = days * 24; // convert days to hours since charges are hourly
          const totalCost = hours * Number(performanceData.charges || 0);
          setTotal(totalCost);
          setReservationFee(Math.ceil(totalCost * 0.1));
        } else if (days > 5) {
          setDuration(days);
          setTotal(0);
          setReservationFee(0);
        } else {
          setDuration(0);
          setTotal(0);
          setReservationFee(0);
        }
      }
    }
  }, [form.from, form.to, form.bookingDate, form.endDate, performanceData.charges, bookingType]);

  const today = new Date().toISOString().split("T")[0];
  
  const formatPhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("0")) return `254${digits.slice(1)}`;
    if (digits.startsWith("254")) return digits;
    return `254${digits}`;
  };

  const validateForm = () => {
    const errs = {};

    if (!form.clientName) errs.clientName = "Enter your name";
    if (!form.phone) errs.phone = "Enter your phone number";
    else if (!/^254\d{9}$/.test(formatPhone(form.phone)))
      errs.phone = "Invalid phone number";

    if (!form.email) errs.email = "Enter your email";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      errs.email = "Invalid email address";

    if (bookingType === "single") {
      if (!form.bookingDate) errs.bookingDate = "Select a date";
      if (!form.from) errs.from = "Select start time";
      if (!form.to) errs.to = "Select end time";
      if (duration <= 0) errs.duration = "Invalid duration";
    } else if (bookingType === "multiple") {
      if (!form.bookingDate) errs.bookingDate = "Select start date";
      if (!form.endDate) errs.endDate = "Select end date";
      if (form.bookingDate && form.endDate) {
        const start = new Date(form.bookingDate);
        const end = new Date(form.endDate);
        if (start > end) errs.endDate = "End date must be after start date";
      }
      const days = Math.ceil((new Date(form.endDate) - new Date(form.bookingDate)) / (1000 * 60 * 60 * 24)) + 1;
      if (days > 5) errs.duration = "Booking limited to 5 days maximum";
      if (days <= 0) errs.duration = "Invalid date range";
    }

    return errs;
  };

  const handleInputChange = (field) => (e) =>
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));

  const checkAvailability = async () => {
    try {
      const params = new URLSearchParams();
      params.append("cardId", performanceData._id);
      params.append("bookingType", bookingType);
      
      if (bookingType === "single") {
        const { bookingDate, from, to } = form;
        params.append("bookingDate", bookingDate);
        params.append("from", from);
        params.append("to", to);
      } else {
        const { bookingDate, endDate } = form;
        params.append("startDate", bookingDate);
        params.append("endDate", endDate);
      }

      const res = await axios.get(
        `${API_URL}/api/performance_bookings/check?${params.toString()}`
      );
      return res.data;
    } catch (err) {
      console.error("Availability check failed", err);
      return {
        available: false,
        reason: err?.response?.data?.message || "Availability check failed"
      };
    }
  };

  const handleBookPerformance = async () => {
    const validation = validateForm();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage("");
    setBookingStatus(null);

    const avail = await checkAvailability();
    if (!avail.available) {
      setErrors({ general: avail.reason });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        cardId: performanceData._id,
        creatorId: performanceData.userId,
        clientName: form.clientName,
        email: form.email,
        phone: formatPhone(form.phone),
        bookingType,
        duration: String(duration),
        totalAmount: total,
        eventDetails: form.eventDetails,
        artType: performanceData.artType,
        artTitle: performanceData.name,
        paymentMethod,
        amount: reservationFee,
      };
      
      if (bookingType === "single") {
        payload.bookingDate = form.bookingDate;
        payload.from = form.from;
        payload.to = form.to;
      } else {
        payload.startDate = form.bookingDate;
        payload.endDate = form.endDate;
      }

      const response = await axios.post( `${API_URL}/api/performance_bookings`, payload );
      const { booking, checkoutRequestId } = response.data;

      setLastBookingId(booking._id);
      setCheckoutRequestId(checkoutRequestId);
      setBookingStatus("pending");
    } catch (err) {
      setErrors({
        general: err.response?.data?.message || "Booking failed.",
      });
      setBookingStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = useCallback(
    (mpesaCode) => {
      setBookingStatus("success");
      if (mpesaCode) setConfirmationCode(mpesaCode);
      setSuccessMessage("Booking confirmed! Confirmation email sent.");

      setTimeout(() => {
        onClose?.();
        onBooked?.();
      }, 9000);
    },
    [onClose, onBooked]
  );

  const handleFailure = useCallback((reason) => {
    setBookingStatus("failed");
    setErrors({ general: reason });
  }, []);

  const handleTimeout = useCallback(() => {
    setBookingStatus("failed");
    setErrors({ general: "Payment timeout. Try again." });
  }, []);

  usePaymentPolling({
    transactionId: lastBookingId,
    type: "performance",
    onSuccess: handleSuccess,
    onFailure: handleFailure,
    onTimeout: handleTimeout,
    pollingKey,
  });

  const handleRetry = async () => {
    setPollingKey((prev) => prev + 1);
    if (!lastBookingId || !checkoutRequestId) return;
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/mpesa/query`, {
        bookingId: lastBookingId,
        checkoutRequestId,
      });

      if (res.data.status === "Success") {
        setBookingStatus("success");
        setConfirmationCode(res.data.mpesaCode);
        setSuccessMessage('Booking confirmed! Confirmation sent to your email.');
        setTimeout(() => onClose(), 9000);
      } else {
        setBookingStatus("failed");
        setErrors({ general: "Payment still not completed." });
      }
    } catch (err) {
      setErrors({
        general: err.response?.data?.message || "Retry failed.",
      });
      setBookingStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle>
        Book {performanceData.name} for {performanceData.artType} Performance
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "75vh", overflowY: "auto", pb: 2 }}>
          {isLoadingDetails && (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={40} />
            </Box>
          )}

          {loadError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {loadError}
            </Alert>
          )}

          {errors.general && (
            <Alert severity="error">{errors.general}</Alert>
          )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

          <PaymentStatusBanner
            status={bookingStatus}
            confirmationCode={confirmationCode}
            onRetry={handleRetry}
          />

          <Grid container spacing={2}  mt={1}>
            <Grid item xs={12}>
              <TextField
                select
                label="Booking Type"
                fullWidth
                value={bookingType}
                onChange={(e) => {
                  setBookingType(e.target.value);
                  setForm(prev => ({
                    ...prev,
                    from: "",
                    to: "",
                    endDate: ""
                  }));
                }}
              >
                <MenuItem value="single">Single Day Booking</MenuItem>
                <MenuItem value="multiple">Multiple Days (Max 5 Days)</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={form.clientName}
                onChange={handleInputChange("clientName")}
                error={Boolean(errors.clientName)}
                helperText={errors.clientName}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={handleInputChange("phone")}
                error={Boolean(errors.phone)}
                helperText={errors.phone}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={form.email}
                onChange={handleInputChange("email")}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>

            {bookingType === "single" ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Booking Date"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    value={form.bookingDate}
                    onChange={handleInputChange("bookingDate")}
                    error={Boolean(errors.bookingDate)}
                    helperText={errors.bookingDate}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={form.from}
                    onChange={handleInputChange("from")}
                    error={Boolean(errors.from)}
                    helperText={errors.from}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={form.to}
                    onChange={handleInputChange("to")}
                    error={Boolean(errors.to)}
                    helperText={errors.to}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Duration (hrs)"
                    value={duration}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    value={form.bookingDate}
                    onChange={handleInputChange("bookingDate")}
                    error={Boolean(errors.bookingDate)}
                    helperText={errors.bookingDate}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: form.bookingDate || today }}
                    value={form.endDate}
                    onChange={handleInputChange("endDate")}
                    error={Boolean(errors.endDate)}
                    helperText={errors.endDate}
                  />
                </Grid>
                
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Duration (days)"
                    value={duration}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                
                {duration > 5 && (
                  <Grid item xs={12}>
                    <Alert severity="error">{errors.duration}</Alert>
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Details / Notes"
                multiline
                minRows={3}
                value={form.eventDetails}
                onChange={handleInputChange("eventDetails")}
              />
            </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <MenuItem value="mpesa">M-Pesa</MenuItem>
              <MenuItem value="card" disabled>
                Debit Card (coming soon)
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Total: Ksh {total}</Typography>
            <Typography variant="subtitle2">
              Reservation Fee: Ksh {reservationFee}
            </Typography>
          </Grid>
          </Grid>
      </DialogContent>

      <DialogActions>
      <Stack direction="row" spacing={2} p={1}  justifyContent= "space-between" width="100%">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleBookPerformance}
          disabled={loading || performanceData.bookingStatus === "closed"}
        >
          {loading ? <CircularProgress size={22}/> : "Book Now"}
        </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PerformanceBookingFormModal;